import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Modal,
  Card,
  Divider,
  Theme,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  ContentCopy,
  UploadFile,
  InsertDriveFile,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: Theme) => ({
  modalContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },
  modal: {
    maxWidth: '600px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '20px 30px',
  },
  modalTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontFamily: 'Montserrat',
  },
  closeButtonIcon: {
    padding: '0px',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  transferLabel: {
    fontFamily: 'Montserrat',
    fontSize: '20px',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  transferAmount: {
    fontFamily: 'Montserrat',
    fontSize: '25px',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  transferSectionTitle: {
    fontFamily: 'Montserrat',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  transferDataWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  transferDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  primaryColor: {
    color: theme.palette.primary.main,
  },
  transferDataText: {
    fontFamily: 'Montserrat',
    fontSize: '16px',
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  copyButton: {
    alignSelf: 'center',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(91, 194, 213, 0.1)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '24px',
    },
  },
  uploadBox: {
    background: '#5BC2D50D',
    backgroundImage: `
      repeating-linear-gradient(0deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px),
      repeating-linear-gradient(90deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px),
      repeating-linear-gradient(180deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px),
      repeating-linear-gradient(270deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px)
    `,
    backgroundSize: '2px 100%, 100% 2px, 2px 100%, 100% 2px',
    backgroundPosition: '0 0, 0 0, 100% 0, 0 100%',
    backgroundRepeat: 'no-repeat',
    borderRadius: theme.shape.borderRadius,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: '#5BC2D520',
      backgroundImage: `
        repeating-linear-gradient(0deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px),
        repeating-linear-gradient(90deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px),
        repeating-linear-gradient(180deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px),
        repeating-linear-gradient(270deg, #2C82B3, #2C82B3 8px, transparent 8px, transparent 16px)
      `,
      backgroundSize: '2px 100%, 100% 2px, 2px 100%, 100% 2px',
      backgroundPosition: '0 0, 0 0, 100% 0, 0 100%',
      backgroundRepeat: 'no-repeat',
    },
  },
  uploadBoxDragging: {
    background: '#5BC2D530',
    backgroundImage: `
      repeating-linear-gradient(0deg, ${theme.palette.primary.main}, ${theme.palette.primary.main} 8px, transparent 8px, transparent 16px),
      repeating-linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.main} 8px, transparent 8px, transparent 16px),
      repeating-linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.main} 8px, transparent 8px, transparent 16px),
      repeating-linear-gradient(270deg, ${theme.palette.primary.main}, ${theme.palette.primary.main} 8px, transparent 8px, transparent 16px)
    `,
    backgroundSize: '2px 100%, 100% 2px, 2px 100%, 100% 2px',
    backgroundPosition: '0 0, 0 0, 100% 0, 0 100%',
    backgroundRepeat: 'no-repeat',
  },
  uploadIcon: {
    fontSize: '48px',
    color: theme.palette.primary.main,
  },
  uploadText: {
    fontFamily: 'Varela Round',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.primary.main,
    textAlign: 'center',
  },
  uploadHelper: {
    fontFamily: 'Varela Round',
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  fileName: {
    fontFamily: 'Varela Round',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.primary.main,
    textAlign: 'center',
  },
  fileSize: {
    fontFamily: 'Varela Round',
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  uploadBoxWithFile: {
    border: '2px solid #2C82B3',
    background: '#5BC2D50D',
    borderRadius: theme.shape.borderRadius,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    position: 'relative',
  },
  fileIcon: {
    fontSize: '48px',
    color: theme.palette.primary.main,
  },
  deleteFileButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    padding: '4px',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: 'rgba(91, 194, 213, 0.1)',
    },
  },
  fileInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  confirmButton: {
    fontFamily: 'Montserrat',
    fontSize: '20px',
    fontWeight: 500,
    textTransform: 'none',
    height: '50px',
  },
}));

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  amount: string;
  onConfirm: (file: File) => Promise<void>;
}

const TransferModal: React.FC<TransferModalProps> = ({
  open,
  onClose,
  amount,
  onConfirm,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copiado al portapapeles`, { variant: 'success' });
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleDeleteFile = () => {
    setFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const sizeInMb = selectedFile.size / (1024 * 1024);
      if (sizeInMb > 10) {
        enqueueSnackbar('El archivo supera los 10 MB permitidos', { variant: 'warning' });
        return;
      }
      if (!['image/png', 'image/jpeg', 'application/pdf'].includes(selectedFile.type)) {
        enqueueSnackbar('Solo se permiten archivos PNG, JPEG o PDF', { variant: 'warning' });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      const sizeInMb = droppedFile.size / (1024 * 1024);
      if (sizeInMb > 10) {
        enqueueSnackbar('El archivo supera los 10 MB permitidos', { variant: 'warning' });
        return;
      }
      if (!['image/png', 'image/jpeg', 'application/pdf'].includes(droppedFile.type)) {
        enqueueSnackbar('Solo se permiten archivos PNG, JPEG o PDF', { variant: 'warning' });
        return;
      }
      setFile(droppedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleConfirm = async () => {
    if (!file) {
      enqueueSnackbar('Debes adjuntar un comprobante de pago', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      await onConfirm(file);
      setFile(null);
    } catch (error) {
      // The error is already handled in the parent component
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFile(null);
      setIsDragging(false);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.modalContainer}
    >
      <Card className={classes.modal}>
        <Box className={classes.modalContent}>
          <Box className={classes.modalTitleContainer}>
            <Typography className={classes.modalTitle}>
              Realizar transferencia
            </Typography>
            <IconButton
              className={classes.closeButtonIcon}
              onClick={handleClose}
            >
              <Close />
            </IconButton>
          </Box>
          <Divider />

          <Box>
            <Typography className={classes.transferLabel}>
              Total a pagar:
            </Typography>
            <Typography className={classes.transferAmount}>
              {amount}
            </Typography>
          </Box>

          <Box>
            <Box className={classes.transferDataWrapper}>
              <Box className={classes.transferDataContainer}>
                <Typography className={classes.transferSectionTitle}>
                  Datos de transferencia:
                </Typography>
                <Typography className={classes.transferDataText}>
                  Asesorías Educacionales Carez y Córdova SpA
                </Typography>
                <Typography className={classes.transferDataText}>
                  76.062.205-2
                </Typography>
                <Typography className={classes.transferDataText}>
                  Banco de Chile
                </Typography>
                <Typography className={classes.transferDataText}>
                  Cuenta Corriente
                </Typography>
                <Typography className={classes.transferDataText}>
                  2370434400
                </Typography>
                <Typography className={classes.transferDataText}>
                  contabilidad@carezycordova.cl
                </Typography>
              </Box>
              <IconButton
                className={classes.copyButton}
                onClick={() => handleCopyToClipboard(
                  'Asesorías Educacionales Carez y Córdova SpA\n76.062.205-2\nBanco de Chile\nCuenta Corriente\n2370434400\ncontabilidad@carezycordova.cl',
                  'Datos de transferencia',
                )}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Typography
            className={`${classes.transferSectionTitle} ${classes.primaryColor}`}
          >
            Adjunta tu comprobante de pago a continuación:
          </Typography>
          <Box>
            {!file ? (
              <Box
                className={`${classes.uploadBox} ${isDragging ? classes.uploadBoxDragging : ''}`}
                onClick={() => document.getElementById('transfer-file-input')?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <UploadFile className={classes.uploadIcon} />
                <Box>
                  <Typography className={classes.uploadText}>
                    Haz clic para subir un archivo o arrástralo aquí
                  </Typography>
                  <Typography className={classes.uploadHelper}>
                    Tamaño máximo de archivo: 10 MB
                  </Typography>
                  <Typography className={classes.uploadHelper}>
                    Solo archivos PNG, JPEG o PDF
                  </Typography>
                </Box>
                <input
                  id="transfer-file-input"
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </Box>
            ) : (
              <Box className={classes.uploadBoxWithFile}>
                <IconButton
                  className={classes.deleteFileButton}
                  onClick={handleDeleteFile}
                  size="small"
                >
                  <Close />
                </IconButton>
                <InsertDriveFile className={classes.fileIcon} />
                <Box className={classes.fileInfoContainer}>
                  <Typography className={classes.fileName}>
                    {file.name}
                  </Typography>
                  <Typography className={classes.fileSize}>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            fullWidth
            onClick={handleConfirm}
            disabled={!file || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {loading ? 'Subiendo comprobante...' : 'Confirmar'}
          </Button>
        </Box>
      </Card>
    </Modal>
  );
};

export default TransferModal;
